import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";

import Modal from "./Modal";
import { Character, Episode } from "../types";
import { characterIds, charactersUrl } from "../utils/rickAndMorty";

const BATCH_SIZE = 12;

interface EpisodeDetailsProps {
  episode: Episode;
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Characters are fetched when the dialog opens, not when the card renders —
 * the old version fired four requests per card, ~80 for a page of episodes.
 * The API takes a comma-separated id list, so a batch is a single request.
 */
const EpisodeDetails: React.FC<EpisodeDetailsProps> = ({
  episode,
  isOpen,
  onClose,
}) => {
  const [characters, setCharacters] = useState<Character[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const allIds = characterIds(episode.characters);

  const loadBatch = useCallback(
    async (from: number) => {
      const ids = allIds.slice(from, from + BATCH_SIZE);
      if (ids.length === 0) return;

      setIsLoading(true);
      setError(null);
      try {
        const { data } = await axios.get(charactersUrl(ids));
        // A single id comes back as an object rather than an array.
        const batch: Character[] = Array.isArray(data) ? data : [data];
        setCharacters((previous) =>
          from === 0 ? batch : [...previous, ...batch]
        );
      } catch {
        setError("Could not load the characters of this episode.");
      } finally {
        setIsLoading(false);
      }
    },
    // allIds is derived from the episode, so the episode id is the real input.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [episode.id]
  );

  useEffect(() => {
    if (!isOpen || characters.length > 0) return;
    loadBatch(0);
  }, [isOpen, characters.length, loadBatch]);

  const hasMore = characters.length < allIds.length;

  return (
    <Modal isOpen={isOpen} title={episode.name} onClose={onClose}>
      <dl className="grid grid-cols-2 gap-3 text-sm">
        <div>
          <dt className="text-slate-400">Air date</dt>
          <dd className="font-medium text-white">{episode.air_date}</dd>
        </div>
        <div>
          <dt className="text-slate-400">Episode</dt>
          <dd className="font-medium text-white">{episode.episode}</dd>
        </div>
      </dl>

      <h3 className="mt-6 text-sm font-semibold uppercase tracking-wide text-slate-400">
        Characters ({allIds.length})
      </h3>

      {error && <p className="mt-3 text-sm text-red-400">{error}</p>}

      <ul className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
        {characters.map((character) => (
          <li
            key={character.id}
            className="flex items-center gap-3 rounded-lg bg-white/5 p-2"
          >
            <img
              src={character.image}
              alt=""
              loading="lazy"
              className="h-10 w-10 rounded-full object-cover"
            />
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-white">
                {character.name}
              </p>
              <p className="truncate text-xs text-slate-400">
                {character.status} · {character.species}
              </p>
            </div>
          </li>
        ))}
      </ul>

      {isLoading && (
        <p className="mt-3 text-sm text-slate-400">Loading characters…</p>
      )}

      {hasMore && !isLoading && (
        <button
          type="button"
          onClick={() => loadBatch(characters.length)}
          className="mt-4 w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm font-medium text-white transition hover:border-portal-400/60 hover:bg-portal-400/10"
        >
          Load {Math.min(BATCH_SIZE, allIds.length - characters.length)} more
        </button>
      )}
    </Modal>
  );
};

export default EpisodeDetails;
